import React, { useState, Fragment, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import { MdBlock, MdCheckCircle } from "react-icons/md";
import DeleteIcon from "assets/icons/DeleteIcon";
import PictureCollection from "components/__reusable/PictureCollection";
import { getTrainingPictures } from "services/trainingPictureServices";
import {
  TrainingPictureOperation,
  CROPPED_PICTURE_FILE_NAME_PREFIX,
} from "config/constants";
import type {
  TrainingPicture,
  UpdatedTrainingPicture,
} from "types/TrainingPicture";
import type { Picture as PictureItem } from "types/PictureCollection";

interface SavedTrainingPicturesProps {
  uploadPictures: File[];
  onUploadPicturesChange: (pictures: File[]) => void;
  updatedTrainingPictures: UpdatedTrainingPicture[];
  onUpdate: (pictures: UpdatedTrainingPicture[]) => void;
}

const SavedTrainingPictures: React.FC<SavedTrainingPicturesProps> = ({
  uploadPictures,
  onUploadPicturesChange,
  updatedTrainingPictures,
  onUpdate,
}) => {
  const { id } = useParams();
  const { t } = useTranslation();
  const [trainingPictures, setTrainingPictures] = useState<TrainingPicture[]>(
    []
  );

  const fetchTrainingPictures = async () => {
    if (isNaN(parseInt(id || ""))) return;
    try {
      const pictures = await getTrainingPictures(id || "");
      setTrainingPictures(pictures);
    } catch (e) {}
  };
  useEffect(() => {
    fetchTrainingPictures();
  }, [id]);

  const usedPictures = trainingPictures.filter(
    (picture) => picture.is_usable && !picture.is_parent_data
  );
  const parentsUploadedPictures = trainingPictures.filter(
    (picture) => picture.is_parent_data
  );
  const usableParentsUploadedPictures = parentsUploadedPictures.filter(
    (picture) => picture.is_usable
  );
  const unusableParentsUploadedPictures = parentsUploadedPictures.filter(
    (picture) => !picture.is_usable
  );
  const [selectedUsedPictures, setSelectedUsedPictures] = useState<
    PictureItem[]
  >([]);
  const [selectedUsableParentsUploadedPictures, setSelectedUsableParentsUploadedPictures] =
    useState<PictureItem[]>([]);
  const [selectedUnusableParentsUploadedPictures, setSelectedUnusableParentsUploadedPictures] =
    useState<PictureItem[]>([]);

  const upsertUpdatedTrainingPicture = (
    updates: UpdatedTrainingPicture[],
    updatedInfo: UpdatedTrainingPicture
  ) => {
    const index = updates.findIndex((picture) => picture.id === updatedInfo.id);
    if (index >= 0) {
      updates[index] = updatedInfo;
    } else {
      updates.push(updatedInfo);
    }
  };

  const updateParentsPictureUsage = (
    shouldUse: boolean,
    selectedPictures: PictureItem[],
    onClearSelection: () => void
  ) => {
    const pictures = [...trainingPictures];
    const temp = [...updatedTrainingPictures];
    let hasUpdate = false;

    for (let picture of selectedPictures) {
      const index = pictures.findIndex((pic) => pic.id === picture.id);
      if (index < 0) continue;
      if (pictures[index].is_usable === shouldUse) continue;

      upsertUpdatedTrainingPicture(temp, {
        id: picture.id,
        operation: shouldUse
          ? TrainingPictureOperation.use
          : TrainingPictureOperation.unuse,
      });
      pictures[index].is_usable = shouldUse;
      hasUpdate = true;
    }

    if (hasUpdate) {
      onUpdate(temp);
      setTrainingPictures(pictures);
    }
    onClearSelection();
  };

  const deletePictures = async (selectedPictures: PictureItem[]) => {
    const pictures = [...trainingPictures];
    const uploadedPictures = [...uploadPictures];
    const temp = [...updatedTrainingPictures];
    for (let picture of selectedPictures) {
      if (picture.id < 1) {
        const index = uploadedPictures.findIndex(
          (pic) =>
            pic.name === `${CROPPED_PICTURE_FILE_NAME_PREFIX}_${picture.id}.png`
        );
        if (index >= 0) uploadedPictures.splice(index, 1);
      } else {
        const index = pictures.findIndex((pic) => pic.id === picture.id);
        if (index < 0) return;
        const updatedInfo: UpdatedTrainingPicture = {
          id: picture.id,
          operation: pictures[index].is_parent_data
            ? TrainingPictureOperation.use
            : TrainingPictureOperation.delete,
        };
        upsertUpdatedTrainingPicture(temp, updatedInfo);
        if (pictures[index].is_parent_data) {
          pictures[index].is_usable = true;
        } else {
          pictures.splice(index, 1);
        }
      }
    }
    onUpdate(temp);
    setTrainingPictures(pictures);
    onUploadPicturesChange(uploadedPictures);
    setSelectedUsedPictures([]);
  };

  return trainingPictures.length > 0 || uploadPictures.length > 0 ? (
    <Fragment>
      <div className="w-full">
        <div className="w-full min-h-[4rem] p-3 bg-white rounded-lg border border-gray-300 shadow-sm">
          <div className="space-y-4">
            <p className="text-sm font-semibold text-[#333333]">
              {t("_student_form._parents_upload_picture")}
            </p>
            {usableParentsUploadedPictures.length > 0 && (
              <div className="space-y-2">
                <div className="w-full flex items-center justify-between">
                  <div />
                  {selectedUsableParentsUploadedPictures.length > 0 && (
                    <button
                      type="button"
                      className="flex items-center space-x-1 text-gray-800 hover:text-red-500 transition-colors"
                      onClick={() =>
                        updateParentsPictureUsage(
                          false,
                          selectedUsableParentsUploadedPictures,
                          () => setSelectedUsableParentsUploadedPictures([])
                        )
                      }
                    >
                      <MdBlock size={24} />
                      <p>{t("_student_form._do_not_use_picture")}</p>
                    </button>
                  )}
                </div>
                <PictureCollection
                  pictures={usableParentsUploadedPictures.map((picture) => ({
                    id: picture.id,
                    picture: picture.original_picture,
                  }))}
                  selectedPictures={selectedUsableParentsUploadedPictures}
                  onSelect={(pictures) => {
                    setSelectedUsableParentsUploadedPictures(pictures);
                    setSelectedUnusableParentsUploadedPictures([]);
                  }}
                />
              </div>
            )}
            {unusableParentsUploadedPictures.length > 0 && (
              <div className="space-y-2 border-t border-gray-200 pt-3">
                <div className="w-full flex items-center justify-between">
                  <p className="text-xs font-semibold text-gray-600">
                    {t("_student_form._ai_excluded_picture_group")}
                  </p>
                  {selectedUnusableParentsUploadedPictures.length > 0 && (
                    <button
                      type="button"
                      className="flex items-center space-x-1 text-green-600 hover:text-green-700 transition-colors"
                      onClick={() =>
                        updateParentsPictureUsage(
                          true,
                          selectedUnusableParentsUploadedPictures,
                          () => setSelectedUnusableParentsUploadedPictures([])
                        )
                      }
                    >
                      <MdCheckCircle size={24} />
                      <p>{t("_student_form._use_picture")}</p>
                    </button>
                  )}
                </div>
                <PictureCollection
                  pictures={unusableParentsUploadedPictures.map((picture) => ({
                    id: picture.id,
                    picture: picture.original_picture,
                    isDisabled: true,
                  }))}
                  selectedPictures={selectedUnusableParentsUploadedPictures}
                  onSelect={(pictures) => {
                    setSelectedUnusableParentsUploadedPictures(pictures);
                    setSelectedUsableParentsUploadedPictures([]);
                  }}
                  allowSelectDisabled={true}
                />
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="w-full">
        <div className="w-full min-h-[4rem] p-3 rounded-lg border border-gray-300 bg-white shadow-sm">
          <p className="mb-3 text-sm font-semibold text-[#333333]">
            {t("_student_form._registration_picture")}
          </p>
          {selectedUsedPictures.length > 0 && (
            <div className="w-full flex items-center justify-end space-x-2">
              <button
                type="button"
                className="flex items-center space-x-1"
                onClick={() => deletePictures(selectedUsedPictures)}
              >
                <DeleteIcon width={24} height={24} color={"black"} />
                <p>{t("_student_form._delete_picture")}</p>
              </button>
            </div>
          )}
          <PictureCollection
            pictures={[
              ...usedPictures.map((picture) => ({
                id: picture.id,
                picture: picture.original_picture,
              })),
              ...uploadPictures.map((pictureFile) => ({
                id: parseFloat(pictureFile.name.split("_")[1]),
                picture: URL.createObjectURL(pictureFile),
              })),
            ]}
            selectedPictures={selectedUsedPictures}
            onSelect={(pictures) => {
              setSelectedUsedPictures(pictures);
            }}
          />
        </div>
      </div>
    </Fragment>
  ) : null;
};

export default SavedTrainingPictures;
